//
// DISCLAIMER
//
// Copyright 2019 ArangoDB GmbH, Cologne, Germany
//
// Author Ewout Prangsma
//

import { Component } from "react";
import { Table, Menu } from "semantic-ui-react";
import apiClients from "../../api/apiclients";
import { ListOptions as ApiListOptions, Organization as ApiOrganization, MemberList as ApiMemberList, Member as ApiMember } from "../../api/lib";
import { RouteComponentProps } from "react-router-dom";
import { ContentSegment, SecondaryMenu, Loading, ErrorMessage, PagingButtons } from "../../ui/lib";
import { withRefresh, IWithRefreshProps } from "../../util/WithRefresh";
import OrganizationMemberRow, { HeaderView } from "./OrganizationMemberRow";

// Interface describing the members
interface IListView extends IWithRefreshProps, RouteComponentProps {
  active: boolean;
  items: ApiMember[];
  loading: boolean;
  page: number;
  pageSize: number;
  onNextPage?: () => void;
  onPreviousPage?: () => void;
  onClickView: (userId: string) => void;
}

const ListView = ({ ...args }: IListView) => (
  <Table striped>
    <HeaderView loading={args.loading} />
    <Table.Body>
      {args.items.map((item) => (
        <OrganizationMemberRow {...args} key={item.user_id} member={item} onMemberSelected={args.onClickView} />
      ))}
    </Table.Body>
    <Table.Footer>
      <Table.Row>
        <Table.HeaderCell colSpan="6">
          <PagingButtons
            size="tiny"
            page={args.page}
            pageSize={args.pageSize}
            count={args.items.length}
            onNextPage={args.onNextPage}
            onPreviousPage={args.onPreviousPage}
          />
        </Table.HeaderCell>
      </Table.Row>
    </Table.Footer>
  </Table>
);

const EmptyView = () => <div>No members inside this organization</div>;

// Interface describing the member list view arguments
export interface IOrganizationMemberListViewArgs extends IWithRefreshProps, RouteComponentProps {
  active: boolean;
  loading: boolean;
  organization: ApiOrganization;
  members?: ApiMemberList;
  page: number;
  pageSize: number;
  onNextPage?: () => void;
  onPreviousPage?: () => void;
  onClickView: (id: string) => void;
}

export const OrganizationMemberListView = ({ ...args }: IOrganizationMemberListViewArgs) => {
  if (!args.members) {
    return <Loading />;
  }
  if (!args.members.items || args.members.items.length === 0) {
    return <EmptyView />;
  }
  return <ListView {...args} items={args.members.items} loading={args.loading} />;
};

// Interface decribing the properties of the member list component
interface IOrganizationMemberListProps extends IWithRefreshProps, RouteComponentProps {
  organization: ApiOrganization;
  onMemberSelected: (userId: string) => void;
  showHeader: boolean;
}

// Interface decribing the state of the member list component
interface IOrganizationMemberListState {
  errorMessage?: string;
  processing: boolean;
  members?: ApiMemberList;
  page: number;
  pageSize: number;
}

// The component to show the members of an organization as a list.
class OrganizationMemberList extends Component<IOrganizationMemberListProps, IOrganizationMemberListState> {
  state: IOrganizationMemberListState = {
    errorMessage: undefined,
    processing: false,
    members: undefined,
    page: 0,
    pageSize: 100,
  };

  reloadMembers = async () => {
    const listOptions: ApiListOptions = { context_id: this.props.organization.id, page: this.state.page, page_size: this.state.pageSize };
    const members = await apiClients.idashboardClient.ListOrganizationMembers(listOptions);
    this.setState({ members: members });
  };

  onNextPage = () => {
    this.setState((prev) => ({ page: prev.page + 1 }), this.reloadMembers);
  };

  onPreviousPage = () => {
    this.setState((prev) => ({ page: prev.page - 1 }), this.reloadMembers);
  };

  componentDidMount() {
    this.props.refreshWithTimer && this.props.refreshWithTimer(this.reloadMembers, 60000);
  }

  handleDismissError = () => {
    this.setState({ errorMessage: undefined });
  };

  render() {
    return (
      <ContentSegment>
        <ErrorMessage active={!!this.state.errorMessage} onDismiss={this.handleDismissError} message={this.state.errorMessage} />
        {this.props.showHeader && (
          <SecondaryMenu>
            <Menu.Item header>Members</Menu.Item>
          </SecondaryMenu>
        )}
        <OrganizationMemberListView
          {...this.props}
          {...this.state}
          active={!this.state.processing}
          onClickView={this.props.onMemberSelected}
          onNextPage={this.onNextPage}
          onPreviousPage={this.state.page > 0 ? this.onPreviousPage : undefined}
        />
      </ContentSegment>
    );
  }
}

export default withRefresh()(OrganizationMemberList);
